import { lazy, Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { Section, SectionTitle } from '@/features/app-shell/section'
import { PlaybookDetailsSkeleton } from '@/features/playbooks/components/details/skeletons'
import { playbookQueries } from '@/features/playbooks/queries'

import { ROLES } from '@/shared/auth/constants'
import { getCurrentTenant } from '@/shared/auth/store'
import { roleAuthGuard } from '@/shared/auth/utils/router'

import { ErrorBoundary } from '@/components/common/error-boundary'

const PlaybookDetails = lazy(
	() => import('@/features/playbooks/components/details'),
)

export const Route = createFileRoute('/_dashboard/playbooks/$id')({
	loaderDeps: ({ search }) => {
		const tenant = search.t

		return { tenant }
	},

	beforeLoad: () => roleAuthGuard(ROLES.Analyst),

	loader: ({ params, context: { queryClient }, deps }) => {
		const { id } = params
		const tenant = deps.tenant ?? getCurrentTenant()

		if (tenant) {
			queryClient.prefetchQuery(playbookQueries.activityDetails(tenant, id))
		}
	},

	head: () => ({
		meta: [
			{
				title: 'Playbook Details | BitLyft AIR®',
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: RoutePendingComponent,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	const { id } = Route.useParams()

	return (
		<Section>
			<SectionTitle>Playbook Details</SectionTitle>

			<ErrorBoundary feature="playbooks" section="details" entityId={id}>
				<Suspense fallback={<PlaybookDetailsSkeleton />}>
					<PlaybookDetails id={id} />
				</Suspense>
			</ErrorBoundary>
		</Section>
	)
}

function RoutePendingComponent() {
	return (
		<Section>
			<SectionTitle>Playbook Details</SectionTitle>

			<PlaybookDetailsSkeleton />
		</Section>
	)
}
